/**
 * PromptPreviewPane
 *
 * Shared renderer for prompt preview sections. Used by both the live
 * editor preview (`PromptEditorView`) and the detail-view preview mode
 * (`PromptDetailView`) so both views display the same shape, labels,
 * and error states with zero duplicated markup. The section list is
 * supplied by the backend's `_rendered_to_response` helper — this
 * component knows nothing about YAML field names.
 */

import React from 'react';
import type { PromptPreviewSection } from '../../types/prompt';

interface Props {
  sections: PromptPreviewSection[] | undefined;
  isLoading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
}

const PromptPreviewPane: React.FC<Props> = ({
  sections,
  isLoading,
  error,
  emptyMessage,
}) => {
  if (error) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
      >
        <pre className="whitespace-pre-wrap font-mono text-xs leading-snug">
          {error.message}
        </pre>
      </div>
    );
  }
  if (isLoading) {
    return <p className="text-sm text-gray-500">Rendering preview…</p>;
  }
  if (!sections || sections.length === 0) {
    return (
      <p className="text-gray-400 text-sm">
        {emptyMessage ?? 'No preview available.'}
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.key}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {section.label}
          </p>
          <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed rounded border bg-gray-50 p-3">
            {section.content}
          </pre>
        </div>
      ))}
    </div>
  );
};

export default PromptPreviewPane;
